import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Form,
  Modal,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import axios from "axios";
import { ConfigStorage } from "../storage/config";
import { Session } from "../storage/session";
import { useServerMessage } from "../utils/serverMessage";
import { Encrypt } from "../utils/encryption";
import { userManagementApi } from "../config/api";
import {
  initFacebookSdk,
  loginWhatsAppEmbeddedSignup,
  subscribeWhatsAppEmbeddedSignupMessages,
  extractEmbeddedSignupFields,
} from "../utils/facebookSdk";
import {
  FACEBOOK_APP_ID,
  FACEBOOK_BUSINESS_ID,
} from "../config/mappedEnv";
import { useAuth } from "../context/AuthContext";

const FB_APP_ID = FACEBOOK_APP_ID;

const waBtnStackStyle = {
  width: "100%",
  display: "flex",
  flexDirection: "row",
  gap: 6,
  minHeight: 32,
  maxHeight: 36,
  alignItems: "stretch",
};

const waBtnStyle = {
  flex: 1,
  minHeight: 0,
  fontSize: 11,
  lineHeight: 1.15,
  padding: "1px 6px",
  height: "auto",
};

export default function ConfigEditor({
  visible = true,
  onSaved,
  onCleared,
  showClear = true,
}) {
  const [waConnecting, setWaConnecting] = useState(false);
  const [waFinalizeBusy, setWaFinalizeBusy] = useState(false);
  const [waStatus, setWaStatus] = useState(null);
  const [waPickerOpen, setWaPickerOpen] = useState(false);
  const [waCandidates, setWaCandidates] = useState([]);
  const [waSetupSessionId, setWaSetupSessionId] = useState(null);
  const [waSelectedKey, setWaSelectedKey] = useState(null);
  const lastEmbeddedMsgRef = useRef(null);
  const { setToken: setAuthToken } = useAuth();
  const { notify, contextHolder } = useServerMessage();

  useEffect(() => {
    const unsub = subscribeWhatsAppEmbeddedSignupMessages((payload) => {
      lastEmbeddedMsgRef.current = payload;
    });
    return unsub;
  }, []);

  const clear = () => {
    ConfigStorage.clear();
    notify("info", "Saved configuration has been cleared.");
    onCleared?.();
  };

  const connectWhatsApp = async () => {
    const session = Session.get();
    if (!session?.token) {
      notify(
        "warning",
        "Your session expired. Please log out and sign in again before connecting WhatsApp."
      );
      return;
    }

    const embeddedConfigId = String(FACEBOOK_BUSINESS_ID);
    if (!FB_APP_ID || !embeddedConfigId) {
      notify(
        "error",
        "Set REACT_APP_1FACEBOOK_APP_ID and REACT_APP_6FB_LOGIN_FOR_BUSINESS_CONFIG_ID / REACT_APP_7META_EMBEDDED_SIGNUP_CONFIG_ID in frontend/.env. " +
        "The config ID is from Meta: App Dashboard → Facebook Login for Business → Configurations " +
        "(WhatsApp Embedded Signup template) — not the App ID. Restart npm after changing .env."
      );
      return;
    }

    setWaConnecting(true);
    lastEmbeddedMsgRef.current = null;
    setWaStatus(null);

    try {
      await initFacebookSdk(FB_APP_ID);
      const fbResponse = await loginWhatsAppEmbeddedSignup(embeddedConfigId);

      const wait = (ms) => new Promise((r) => setTimeout(r, ms));
      await wait(400);
      let fields = extractEmbeddedSignupFields(
        fbResponse,
        lastEmbeddedMsgRef.current
      );
      if (!fields.metaCode) {
        await wait(1200);
        fields = extractEmbeddedSignupFields(
          fbResponse,
          lastEmbeddedMsgRef.current
        );
      }

      if (!fields.metaCode) {
        notify(
          "error",
          "Meta did not return an authorization code. Finish the Embedded Signup steps in the Facebook window, then try again."
        );
        return;
      }

      const redirectUri =
        typeof window !== "undefined" ? window.location.href : "";

      const discoverRes = await axios.post(
        userManagementApi,
        {
          ...Encrypt({
            token: session.token,
            metaCode: fields.metaCode,
            wabaId: fields.wabaId,
            phoneNumberId: fields.phoneNumberId,
            businessPortfolioId: fields.businessPortfolioId,
            redirectUri,
            oauthStyle: "embedded",
          }),
          operationType: "whatsappWaDiscover",
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const candidates = discoverRes.data?.candidates || [];
      const setupSessionId = discoverRes.data?.setupSessionId;

      if (!setupSessionId || candidates.length === 0) {
        notify(
          "error",
          discoverRes.data?.message ||
          "No WhatsApp numbers were found for this Meta account."
        );
        return;
      }

      setWaSetupSessionId(setupSessionId);
      setWaCandidates(candidates);
      setWaSelectedKey(null);
      setWaPickerOpen(true);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Could not connect WhatsApp.";
      notify("error", msg);
    } finally {
      setWaConnecting(false);
    }
  };

  const rowKey = (r) =>
    `${r.phoneNumberId}__${r.wabaId}`;

  const finalizeEmbeddedChoice = async () => {
    const session = Session.get();
    if (!session?.token || !waSetupSessionId || !waSelectedKey) {
      notify("warning", "Select a production phone number first.");
      return;
    }

    const selected = waCandidates.find(
      (c) => rowKey(c) === waSelectedKey
    );
    if (!selected?.selectable) {
      notify("error", "That number cannot be used.");
      return;
    }

    setWaFinalizeBusy(true);
    try {
      const res = await axios.post(
        userManagementApi,
        {
          ...Encrypt({
            token: session.token,
            setupSessionId: waSetupSessionId,
            phoneNumberId: selected.phoneNumberId,
            wabaId: selected.wabaId,
          }),
          operationType: "whatsappWaFinalize",
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const {
        token: newToken,
        message,
      } = res.data || {};

      if (newToken) {
        Session.save({
          ...session,
          token: newToken,
          status: session.status || "success",
        });
        setAuthToken(newToken);
      }

      setWaPickerOpen(false);
      setWaCandidates([]);
      setWaSetupSessionId(null);
      setWaSelectedKey(null);
      setWaStatus({
        wabaId: selected.wabaId,
        phoneNumberId: selected.phoneNumberId,
      });
      notify("success", message || "WhatsApp Business connected.");
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Could not finalize WhatsApp.";
      notify("error", msg);
    } finally {
      setWaFinalizeBusy(false);
    }
  };

  const addExistingWhatsApp = () => {
    connectWhatsApp();
  };

  const pickerColumns = [
    {
      title: "Phone",
      key: "display",
      render: (_, r) => (
        <span>{r.displayPhoneNumber || r.phoneNumberId}</span>
      ),
    },
    {
      title: "Mode",
      key: "mode",
      width: 110,
      render: (_, r) =>
        r.numberType === "test" ||
          String(r.accountMode || "").toUpperCase() === "SANDBOX" ? (
          <Tag color="orange">Test</Tag>
        ) : (
          <Tag color="green">Live</Tag>
        ),
    },
    {
      title: "WABA",
      key: "waba",
      ellipsis: true,
      render: (_, r) => (
        <Tooltip title={r.wabaId}>{String(r.wabaId || "").slice(0, 14)}…</Tooltip>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Modal
        title="Pick your WhatsApp Business number"
        open={waPickerOpen}
        onCancel={() => {
          setWaPickerOpen(false);
          setWaCandidates([]);
          setWaSetupSessionId(null);
          setWaSelectedKey(null);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setWaPickerOpen(false);
              setWaCandidates([]);
              setWaSetupSessionId(null);
              setWaSelectedKey(null);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="ok"
            type="primary"
            loading={waFinalizeBusy}
            disabled={!waSelectedKey}
            onClick={finalizeEmbeddedChoice}
          >
            Connect this number
          </Button>,
        ]}
        width={640}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Choose your WhatsApp Business number. Test numbers are marked
          with an orange tag.
        </Typography.Paragraph>
        <Table
          rowKey={rowKey}
          size="small"
          pagination={waCandidates.length > 6 ? { pageSize: 6 } : false}
          columns={pickerColumns}
          dataSource={waCandidates}
          rowSelection={{
            type: "radio",
            selectedRowKeys: waSelectedKey ? [waSelectedKey] : [],
            onChange: (_keys, rows) => {
              const row = rows[0];
              setWaSelectedKey(row ? rowKey(row) : null);
            },
          }}
          onRow={(record) => ({
            onClick: () => {
              setWaSelectedKey(rowKey(record));
            },
          })}
        />
      </Modal>

      <Form layout="vertical" className="auth-form">
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <div style={waBtnStackStyle}>
            <Tooltip title="Connect through Meta Embedded Signup">
              <span style={{ flex: 1, display: 'flex' }}>
                <Button
                  type="default"
                  size="small"
                  style={{ ...waBtnStyle, width: "100%" }}
                  loading={waConnecting}
                  disabled={waConnecting}
                  onClick={connectWhatsApp}
                >
                  Create & Connect Account
                </Button>
              </span>
            </Tooltip>
            <Button
              type="default"
              size="small"
              style={waBtnStyle}
              disabled={waConnecting}
              onClick={addExistingWhatsApp}
            >
              Connect Existing Account
            </Button>
          </div>

          {showClear ? (
            <Button type="default" danger block onClick={clear}>
              Clear saved configuration
            </Button>
          ) : null}
        </Space>
      </Form>

      {waStatus && (waStatus.wabaId || waStatus.phoneNumberId) ? (
        <Typography.Paragraph className="auth-helper" style={{ marginTop: 12 }}>
          WhatsApp (saved on your account): WABA{" "}
          {waStatus.wabaId || "—"}, phone number ID{" "}
          {waStatus.phoneNumberId || "—"}.
        </Typography.Paragraph>
      ) : null}

      <Typography.Text className="auth-helper">
        Connect to get started.
      </Typography.Text>
    </>
  );
}
